<template>
  <validation-observer
      ref="refFormObserver"
      #default="{ handleSubmit, invalid }"
  >
    <!-- Status Info: Input Fields -->
    <b-form
        @submit.prevent="handleSubmit(submitData)"
        @reset.prevent="resetForm"
    >
      <b-row>
        <!-- Field: label -->
        <b-col cols="12" md="4">
          <validation-provider
              #default="validationContext"
              name="label"
              rules="required"
          >
            <b-form-group label="Libellé" label-for="label">
              <b-form-input
                  id="label"
                  v-model="brand.name"
                  type="text"
              />
              <b-form-invalid-feedback
                  :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>

      <b-row>
        <!-- Field: Description -->
        <b-col cols="12" md="4">
          <!-- details -->
          <validation-provider #default="validationContext" name="description">
            <b-form-group label="Description" label-for="description">
              <b-form-textarea
                  id="description"
                  v-model="brand.description"
              />
              <b-form-invalid-feedback
                  :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>

      <!-- Action Buttons -->
      <b-button
          :block="$store.getters['app/currentBreakPoint'] === 'xs'"
          :disabled="isLoading || invalid"
          class="mb-1 mb-sm-0 mr-0 mr-sm-1"
          type="submit"
          variant="primary"
      >
        <b-spinner v-if="isLoading" small></b-spinner>
        {{ brand.uid ? "Modifier" : "Sauvegarder" }}
      </b-button>
      <b-button
          :block="$store.getters['app/currentBreakPoint'] === 'xs'"
          variant="outline-secondary"
          @click="() => $router.go(-1)"
      >
        Annuler
      </b-button>
    </b-form>
  </validation-observer>
</template>

<script>
import {
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormTextarea,
  BRow,
  BSpinner,
} from "bootstrap-vue";
import {ValidationObserver, ValidationProvider} from "vee-validate";

import {computed, ref, watch} from "@vue/composition-api";
import {heightFade} from "@core/directives/animations";
import formValidation from "@core/comp-functions/forms/form-validation";
import store from "@/store";
import {required} from "@validations";

import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {useToast} from "vue-toastification/composition";

export default {
  name: "HandleStoreTabGeneral",
  components: {
    BSpinner,
    BFormTextarea,
    BFormInvalidFeedback,
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    brandData: {
      type: Object,
      required: true,
    },
  },
  emit: ['data-handled'],
  directives: {
    "height-fade": heightFade,
  },
  setup(props, {emit}) {
    const toast = useToast();

    const isLoading = ref(false);
    const brand = ref(JSON.parse(JSON.stringify(props.brandData)));

    const resetBrandLocal = () => {
      brand.value = props.brandData;
    };

    const localBrand = computed(() => (props.brandData))

    watch(localBrand, () => {
      resetBrandLocal()
    }, {deep: true});

    const showToast = (messages, icon, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title: messages,
          icon: icon,
          variant: variant,
        },
      });
    };

    const getError = computed(() => {
      return store.getters["itemBrand/errors"];
    });

    const resetError = () => store.commit("itemBrand/SET_ERRORS", null);

    const {refFormObserver, getValidationState, resetForm, clearForm} =
        formValidation(resetBrandLocal);

    const handleSuccess = (message) => {
      emit("data-handled");
      showToast(message, "CheckIcon", "success");
    };

    const handleErrors = (errors) => {
      for (const [field, message] of Object.entries(errors)) {
        refFormObserver.value.setErrors({[field]: message[0]});
      }
    };

    watch(getError, (val) => {
      if (val) {
        showToast(val.message, "AlertTriangleIcon", "danger");
        handleErrors(val.error);
      }
    });

    const submitData = async () => {
      isLoading.value = true;
      resetError();

      try {
        if (brand.value.uid) {
          const {uid, name, description} = brand.value;
          const {message} = await store.dispatch(
              "itemBrand/updateBrand",
              {
                id: uid,
                data: { name, description }
              }
          );
          handleSuccess(message);
        } else {
          const {message} = await store.dispatch("itemBrand/addBrand", {
            data: brand.value,
          });
          handleSuccess(message);
        }
        clearForm();
      } catch (e) {
        console.log(
            "Error while submitting form: ",
            e.response ? e.response.data.message : e.message)
      } finally {
        resetError();
        isLoading.value = false;
      }
    };

    return {
      required,
      isLoading,
      brand,
      submitData,
      refFormObserver,

      resetForm,
      getValidationState,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
